// Create/Edit Horse Page
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { horseAPI, crmAPI } from '../../services/api';
import api from '../../services/api';
import { useOnboarding } from '../../context/OnboardingContext';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectLabel,
  SelectGroup,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Save } from 'lucide-react';
import { useDateInput } from '../../hooks/useDateInput';

const colors = [
  { value: 'bay', label: 'Bay' },
  { value: 'black', label: 'Black' },
  { value: 'chestnut', label: 'Chestnut' },
  { value: 'dark_bay_brown', label: 'Dark Bay/Brown' },
  { value: 'gray_roan', label: 'Gray/Roan' },
  { value: 'palomino', label: 'Palomino' },
  { value: 'white', label: 'White' },
];

const areasUS = [
  'Arizona', 'California', 'Delaware', 'Florida', 'Illinois', 'Indiana',
  'Kentucky', 'Louisiana', 'Maryland', 'Massachusetts', 'Minnesota', 'Montana',
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'Ohio', 'Oregon',
  'Pennsylvania', 'Puerto Rico', 'Texas', 'Virginia', 'West Virginia', 'Wyoming', 'US'
];

const areasCanada = [
  'Alberta', 'British Columbia', 'Ontario', 'Saskatchewan', 'Canada'
];

const areasIntl = [
  'Australia', 'France', 'Ireland', 'Japan', 'UAE', 'Other'
];

/** Combo input: type a name OR pick from existing horses in system */
function DamSirePicker({ label, value, onChange, onIdChange, horses, placeholder }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value || '');
  const ref = useRef();

  useEffect(() => { setQ(value || ''); }, [value]);

  const filtered = horses.filter(h =>
    !q || (h.system_name || h.name || '').toLowerCase().includes(q.toLowerCase())
  ).slice(0, 8);

  const select = (h) => {
    const name = h.system_name || h.name || '';
    setQ(name);
    onChange(name);
    onIdChange(h.id);
    setOpen(false);
  };

  const handleChange = (e) => {
    setQ(e.target.value);
    onChange(e.target.value);
    onIdChange(null); // clear link when typing manually
    setOpen(true);
  };

  return (
    <div className="relative" ref={ref}>
      <Input
        value={q}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 w-full bg-white border rounded shadow-md mt-1 max-h-48 overflow-y-auto">
          {filtered.map(h => (
            <div key={h.id}
              className="px-3 py-2 text-sm hover:bg-muted/40 cursor-pointer flex items-center justify-between"
              onMouseDown={() => select(h)}>
              <span className="font-medium">{h.system_name || h.name}</span>
              <span className="text-xs text-muted-foreground ml-2">{h.gender} · {h.age}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const HorseFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { completeItem } = useOnboarding();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allHorses, setAllHorses] = useState([]);
  
  const [formData, setFormData] = useState({
    // Basic Info Only for New Horse
    name: '',
    unnamed: false,
    sex: 'male',
    birthdate: '',
    color: '',
    area_foaled: '',
    microchip_id: '',
    sire_name: '',
    sire_id: null,
    dam_name: '',
    dam_id: null,
    dam_year_foaled: '',
    // Conditional fields
    still_nursing: false,
    separated_from_dam: false,
    castrated: false,
    cryptorchid: false,
    horse_type_male: '',
    horse_type_female: '',
    cover_status: '',
  });

  // Use date input hook for proper styling
  const dobInputRef = useDateInput(formData.birthdate);

  useEffect(() => {
    if (isEdit) {
      fetchHorse();
    }
    // Always fetch horse list for dam/sire linking
    api.get('/horses').then(r => setAllHorses(r.data || [])).catch(() => {});
  }, [id]);

  const fetchHorse = async () => {
    setLoading(true);
    try {
      const response = await horseAPI.get(id);
      // Format birthdate for input
      const data = response.data;
      if (data.birthdate) {
        data.birthdate = data.birthdate.split('T')[0];
      }
      setFormData({ ...formData, ...data });
    } catch (error) {
      toast.error('Failed to load horse');
      navigate('/horses');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const data = { ...formData };
      
      // Format birthdate
      if (data.birthdate) {
        try {
          const d = new Date(data.birthdate);
          if (isNaN(d.getTime())) {
            toast.error('Please enter a valid birthdate');
            setSaving(false);
            return;
          }
          // Use T12:00:00.000Z to avoid UTC rollback for US timezones
          data.birthdate = `${data.birthdate}T12:00:00.000Z`;
        } catch {
          toast.error('Please enter a valid birthdate');
          setSaving(false);
          return;
        }
      } else {
        toast.error('Birthdate is required');
        setSaving(false);
        return;
      }
      
      // Convert empty strings to null for optional/enum fields
      const nullIfEmpty = ['color', 'area_foaled', 'microchip_id', 'horse_type_male', 
        'horse_type_female', 'cover_status', 'dam_name', 'sire_name', 'name',
        'boarding_location', 'vices_issues_notes', 'other_notes', 'other_pedigree_notes',
        'pedigree_reference_link', 'jockey_club_registration'];
      nullIfEmpty.forEach(key => { if (data[key] === '') data[key] = null; });
      if (data.dam_year_foaled === '' || data.dam_year_foaled === null) data.dam_year_foaled = null;
      else if (data.dam_year_foaled) data.dam_year_foaled = parseInt(data.dam_year_foaled);

      if (isEdit) {
        await horseAPI.update(id, data);
        toast.success('Horse updated successfully');
      } else {
        await horseAPI.create(data);
        toast.success('Horse created successfully');
        if (!isEdit) completeItem('add_horse');
      }
      navigate('/horses');
    } catch (error) {
      console.error('Save error:', error);
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg || d).join(', '));
      } else {
        toast.error(detail || 'Failed to save horse. Please check all required fields.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="horse-form">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/horses')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="font-serif text-3xl font-semibold">
                {isEdit ? 'Edit Horse' : 'Add Horse'}
              </h1>
            </div>
          </div>
          <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-btn">
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save Horse</>
            )}
          </Button>
        </div>

        {/* Simplified form for new horses, full tabs for editing */}
        {isEdit ? (
          /* Edit Mode: Show Tabs */
          <Tabs defaultValue="basic" className="space-y-4">
            <TabsList>
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="pedigree">Pedigree</TabsTrigger>
              <TabsTrigger value="breeding">Breeding</TabsTrigger>
              <TabsTrigger value="vices">Vices & Issues</TabsTrigger>
            </TabsList>

            {/* Basic Info Tab */}
            <TabsContent value="basic">
            <Card>
              <CardContent className="pt-6 space-y-6">
                {/* Name */}
                <div className="flex items-center space-x-2 mb-4">
                  <Checkbox
                    id="unnamed"
                    checked={formData.unnamed}
                    onCheckedChange={(checked) => handleChange('unnamed', checked)}
                  />
                  <Label htmlFor="unnamed">Unnamed</Label>
                </div>

                {!formData.unnamed && (
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      required={!formData.unnamed}
                      data-testid="horse-name-input"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sex">Sex *</Label>
                    <Select
                      value={formData.sex}
                      onValueChange={(v) => handleChange('sex', v)}
                    >
                      <SelectTrigger data-testid="sex-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="birthdate">Date of Birth *</Label>
                    <Input
                      id="birthdate"
                      type="date"
                      ref={dobInputRef}
                      value={formData.birthdate}
                      onChange={(e) => handleChange('birthdate', e.target.value)}
                      required
                      data-testid="birthdate-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="color">Color</Label>
                    <Select
                      value={formData.color}
                      onValueChange={(v) => handleChange('color', v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select color" />
                      </SelectTrigger>
                      <SelectContent>
                        {colors.map((color) => (
                          <SelectItem key={color.value} value={color.value}>
                            {color.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="area_foaled">Area Foaled</Label>
                    <Select
                      value={formData.area_foaled}
                      onValueChange={(v) => handleChange('area_foaled', v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>United States</SelectLabel>
                          {areasUS.map((area) => (
                            <SelectItem key={area} value={area}>{area}</SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>Canada</SelectLabel>
                          {areasCanada.map((area) => (
                            <SelectItem key={area} value={area}>{area}</SelectItem>
                          ))}
                        </SelectGroup>
                        <SelectGroup>
                          <SelectLabel>International</SelectLabel>
                          {areasIntl.map((area) => (
                            <SelectItem key={area} value={area}>{area}</SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="microchip_id">Microchip ID</Label>
                    <Input
                      id="microchip_id"
                      value={formData.microchip_id}
                      onChange={(e) => handleChange('microchip_id', e.target.value)}
                      className="font-mono"
                    />
                  </div>
                </div>

                {/* Male-specific */}
                {formData.sex === 'male' && (
                  <div className="border-t pt-4">
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="castrated"
                          checked={formData.castrated}
                          onCheckedChange={(checked) => handleChange('castrated', checked)}
                        />
                        <Label htmlFor="castrated">Castrated (Gelding)</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="cryptorchid"
                          checked={formData.cryptorchid}
                          onCheckedChange={(checked) => handleChange('cryptorchid', checked)}
                        />
                        <Label htmlFor="cryptorchid">Cryptorchid (Ridgeling)</Label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Young horse status */}
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Foal Status</h3>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="still_nursing"
                        checked={formData.still_nursing}
                        onCheckedChange={(checked) => handleChange('still_nursing', checked)}
                      />
                      <Label htmlFor="still_nursing">Still nursing</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="separated_from_dam"
                        checked={formData.separated_from_dam}
                        onCheckedChange={(checked) => handleChange('separated_from_dam', checked)}
                      />
                      <Label htmlFor="separated_from_dam">Separated from dam (weaned)</Label>
                    </div>
                  </div>
                </div>

                {/* Registration */}
                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Registration & Engagements</h3>
                  <div className="space-y-2 mb-4">
                    <Label htmlFor="jockey_club_registration">Jockey Club Registration #</Label>
                    <Input
                      id="jockey_club_registration"
                      value={formData.jockey_club_registration}
                      onChange={(e) => handleChange('jockey_club_registration', e.target.value)}
                      className="font-mono max-w-xs"
                    />
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="engagements_breeders_cup"
                        checked={formData.engagements_breeders_cup}
                        onCheckedChange={(checked) => handleChange('engagements_breeders_cup', checked)}
                      />
                      <Label htmlFor="engagements_breeders_cup">Breeders' Cup</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="engagements_ntra"
                        checked={formData.engagements_ntra}
                        onCheckedChange={(checked) => handleChange('engagements_ntra', checked)}
                      />
                      <Label htmlFor="engagements_ntra">NTRA</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="engagements_ktdf"
                        checked={formData.engagements_ktdf}
                        onCheckedChange={(checked) => handleChange('engagements_ktdf', checked)}
                      />
                      <Label htmlFor="engagements_ktdf">KTDF</Label>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <Label htmlFor="boarding_location">Current Boarding Location</Label>
                  <Input
                    id="boarding_location"
                    value={formData.boarding_location}
                    onChange={(e) => handleChange('boarding_location', e.target.value)}
                  />
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="other_notes">Notes</Label>
                  <Textarea
                    id="other_notes"
                    value={formData.other_notes}
                    onChange={(e) => handleChange('other_notes', e.target.value)}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pedigree Tab */}
          <TabsContent value="pedigree">
            <Card>
              <CardHeader>
                <CardTitle className="font-serif text-lg">3-Generation Pedigree</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Sire side */}
                  <div className="space-y-4 p-4 bg-blue-50 border border-blue-200">
                    <h3 className="font-semibold text-blue-800">Sire (Father)</h3>
                    <div className="space-y-2">
                      <Label htmlFor="sire_name">Sire Name</Label>
                      <Input
                        id="sire_name"
                        value={formData.sire_name}
                        onChange={(e) => handleChange('sire_name', e.target.value)}
                        data-testid="sire-name-input"
                      />
                    </div>
                    <div className="pl-4 space-y-4 border-l-2 border-blue-300">
                      <div className="space-y-2">
                        <Label htmlFor="sire_grandsire_name">Sire's Sire (Grandsire)</Label>
                        <Input
                          id="sire_grandsire_name"
                          value={formData.sire_grandsire_name}
                          onChange={(e) => handleChange('sire_grandsire_name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="sire_granddam_name">Sire's Dam (Granddam)</Label>
                        <Input
                          id="sire_granddam_name"
                          value={formData.sire_granddam_name}
                          onChange={(e) => handleChange('sire_granddam_name', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Dam side */}
                  <div className="space-y-4 p-4 bg-pink-50 border border-pink-200">
                    <h3 className="font-semibold text-pink-800">Dam (Mother)</h3>
                    <div className="space-y-2">
                      <Label htmlFor="dam_name">Dam Name</Label>
                      <Input
                        id="dam_name"
                        value={formData.dam_name}
                        onChange={(e) => handleChange('dam_name', e.target.value)}
                        data-testid="dam-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="dam_year_foaled">Dam Year Foaled</Label>
                      <Input
                        id="dam_year_foaled"
                        type="number"
                        min="1990"
                        max="2030"
                        value={formData.dam_year_foaled}
                        onChange={(e) => handleChange('dam_year_foaled', e.target.value)}
                      />
                    </div>
                    <div className="pl-4 space-y-4 border-l-2 border-pink-300">
                      <div className="space-y-2">
                        <Label htmlFor="dam_grandsire_name">Dam's Sire (Damsire/Broodmare Sire)</Label>
                        <Input
                          id="dam_grandsire_name"
                          value={formData.dam_grandsire_name}
                          onChange={(e) => handleChange('dam_grandsire_name', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="dam_granddam_name">Dam's Dam (Granddam)</Label>
                        <Input
                          id="dam_granddam_name"
                          value={formData.dam_granddam_name}
                          onChange={(e) => handleChange('dam_granddam_name', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pedigree_reference_link">Pedigree Reference Link</Label>
                  <Input
                    id="pedigree_reference_link"
                    value={formData.pedigree_reference_link}
                    onChange={(e) => handleChange('pedigree_reference_link', e.target.value)}
                    placeholder="https://www.bloodhorse.com/..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="other_pedigree_notes">Pedigree Notes</Label>
                  <Textarea
                    id="other_pedigree_notes"
                    value={formData.other_pedigree_notes}
                    onChange={(e) => handleChange('other_pedigree_notes', e.target.value)}
                    rows={3}
                    placeholder="Notable ancestors, family characteristics, etc."
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Breeding Tab */}
          <TabsContent value="breeding">
            <Card>
              <CardContent className="pt-6 space-y-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="bred_and_raised"
                    checked={formData.bred_and_raised}
                    onCheckedChange={(checked) => handleChange('bred_and_raised', checked)}
                  />
                  <Label htmlFor="bred_and_raised">Bred and raised by current owner</Label>
                </div>

                {formData.sex === 'female' && (
                  <div className="border-t pt-4">
                    <h3 className="font-medium mb-3">Mare Status</h3>
                    <div className="flex items-center space-x-2 mb-4">
                      <Checkbox
                        id="being_bred"
                        checked={formData.being_bred}
                        onCheckedChange={(checked) => handleChange('being_bred', checked)}
                      />
                      <Label htmlFor="being_bred">Currently being bred</Label>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cover_status">Cover Status</Label>
                      <Select
                        value={formData.cover_status}
                        onValueChange={(v) => handleChange('cover_status', v)}
                      >
                        <SelectTrigger className="max-w-xs">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="maiden">Maiden</SelectItem>
                          <SelectItem value="bred">Bred</SelectItem>
                          <SelectItem value="in_foal">In Foal</SelectItem>
                          <SelectItem value="barren">Barren</SelectItem>
                          <SelectItem value="not_pregnant">Not Pregnant</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {formData.sex === 'male' && !formData.castrated && (
                  <div className="border-t pt-4">
                    <h3 className="font-medium mb-3">Stallion Status</h3>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="standing_at_stud"
                        checked={formData.standing_at_stud}
                        onCheckedChange={(checked) => handleChange('standing_at_stud', checked)}
                      />
                      <Label htmlFor="standing_at_stud">Standing at stud</Label>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vices & Issues Tab */}
          <TabsContent value="vices">
            <Card>
              <CardContent className="pt-6 space-y-6">
                <div>
                  <h3 className="font-medium mb-3">Vices</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                      { id: 'vices_cribber', label: 'Cribber' },
                      { id: 'vices_weaving', label: 'Weaving' },
                      { id: 'vices_stall_walking', label: 'Stall Walking' },
                      { id: 'vices_pawing', label: 'Pawing' },
                      { id: 'vices_wood_chewing', label: 'Wood Chewing' },
                      { id: 'vices_wall_kicking', label: 'Wall Kicking' },
                    ].map((vice) => (
                      <div key={vice.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={vice.id}
                          checked={formData[vice.id]}
                          onCheckedChange={(checked) => handleChange(vice.id, checked)}
                        />
                        <Label htmlFor={vice.id}>{vice.label}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-medium mb-3">Other Disclaimers</h3>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox id="other_issues_eye_certification" checked={formData.other_issues_eye_certification}
                        onCheckedChange={(checked) => handleChange('other_issues_eye_certification', checked)} />
                      <Label htmlFor="other_issues_eye_certification">Eye Certification Issue</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="other_issues_surgery" checked={formData.other_issues_surgery}
                        onCheckedChange={(checked) => handleChange('other_issues_surgery', checked)} />
                      <Label htmlFor="other_issues_surgery">Previous Surgery</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="other_issues_regumate" checked={formData.other_issues_regumate}
                        onCheckedChange={(checked) => handleChange('other_issues_regumate', checked)} />
                      <Label htmlFor="other_issues_regumate">Regumate</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox id="other_issues_shoes_for_sale" checked={formData.other_issues_shoes_for_sale}
                        onCheckedChange={(checked) => handleChange('other_issues_shoes_for_sale', checked)} />
                      <Label htmlFor="other_issues_shoes_for_sale">Shoes for the Sale</Label>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vices_issues_notes">Notes on Vices/Disclaimers</Label>
                  <Textarea
                    id="vices_issues_notes"
                    value={formData.vices_issues_notes}
                    onChange={(e) => handleChange('vices_issues_notes', e.target.value)}
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        ) : (
          /* Create Mode: Basic Info Only */
          <Card>
            <CardContent className="pt-6 space-y-6">
              {/* Name */}
              <div className="flex items-center space-x-2 mb-4">
                <Checkbox
                  id="unnamed"
                  checked={formData.unnamed}
                  onCheckedChange={(checked) => handleChange('unnamed', checked)}
                />
                <Label htmlFor="unnamed">Unnamed</Label>
              </div>

              {!formData.unnamed && (
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    required={!formData.unnamed}
                    placeholder="Enter horse name"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sex">Sex *</Label>
                  <Select
                    value={formData.sex}
                    onValueChange={(v) => handleChange('sex', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="birthdate">Date of Birth *</Label>
                  <Input
                    id="birthdate"
                    type="date"
                    value={formData.birthdate}
                    onChange={(e) => handleChange('birthdate', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="color">Color</Label>
                  <Select
                    value={formData.color}
                    onValueChange={(v) => handleChange('color', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select color" />
                    </SelectTrigger>
                    <SelectContent>
                      {colors.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {c.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="area_foaled">Area Foaled</Label>
                  <Select
                    value={formData.area_foaled}
                    onValueChange={(v) => handleChange('area_foaled', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select area" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>United States</SelectLabel>
                        {areasUS.map((area) => (
                          <SelectItem key={area} value={area}>
                            {area}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel>Canada</SelectLabel>
                        {areasCanada.map((area) => (
                          <SelectItem key={area} value={area}>
                            {area}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel>International</SelectLabel>
                        {areasIntl.map((area) => (
                          <SelectItem key={area} value={area}>
                            {area}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="microchip_id">Microchip ID</Label>
                <Input
                  id="microchip_id"
                  value={formData.microchip_id}
                  onChange={(e) => handleChange('microchip_id', e.target.value)}
                  placeholder="Optional"
                />
              </div>

              <div className="space-y-2">
                <Label>Sire (Father)</Label>
                <DamSirePicker
                  label="Sire"
                  value={formData.sire_name}
                  onChange={v => handleChange('sire_name', v)}
                  onIdChange={v => handleChange('sire_id', v)}
                  horses={allHorses.filter(h => h.sex === 'male')}
                  placeholder="Type or select sire…"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Dam (Mother)</Label>
                  <DamSirePicker
                    label="Dam"
                    value={formData.dam_name}
                    onChange={v => handleChange('dam_name', v)}
                    onIdChange={v => handleChange('dam_id', v)}
                    horses={allHorses.filter(h => h.sex === 'female')}
                    placeholder="Type or select dam…"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Dam Year Foaled</Label>
                  <Input
                    type="number"
                    value={formData.dam_year_foaled}
                    onChange={(e) => handleChange('dam_year_foaled', e.target.value)}
                    placeholder="e.g., 2010"
                  />
                </div>
              </div>

              {/* Conditional: If foal born this year */}
              {formData.birthdate && new Date(formData.birthdate).getFullYear() === new Date().getFullYear() && (
                <div className="border-t pt-4">
                  <Label className="mb-3 block">Foal Status</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="still_nursing"
                        checked={formData.still_nursing}
                        onCheckedChange={(checked) => handleChange('still_nursing', checked)}
                      />
                      <Label htmlFor="still_nursing">Still Nursing</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="separated_from_dam"
                        checked={formData.separated_from_dam}
                        onCheckedChange={(checked) => handleChange('separated_from_dam', checked)}
                      />
                      <Label htmlFor="separated_from_dam">Separated from Dam</Label>
                    </div>
                  </div>
                </div>
              )}

              {/* Conditional: If male */}
              {formData.sex === 'male' && (
                <div className="border-t pt-4">
                  <Label className="mb-3 block">Male Status</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="castrated"
                        checked={formData.castrated}
                        onCheckedChange={(checked) => handleChange('castrated', checked)}
                      />
                      <Label htmlFor="castrated">Castrated (Gelding)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="cryptorchid"
                        checked={formData.cryptorchid}
                        onCheckedChange={(checked) => handleChange('cryptorchid', checked)}
                      />
                      <Label htmlFor="cryptorchid">Cryptorchid</Label>
                    </div>
                  </div>
                  
                  <div className="space-y-2 mt-4">
                    <Label htmlFor="horse_type_male">Horse Type</Label>
                    <Select
                      value={formData.horse_type_male}
                      onValueChange={(v) => handleChange('horse_type_male', v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="racing_stallion_prospect">Racing / Stallion Prospect</SelectItem>
                        <SelectItem value="stallion_prospect">Stallion Prospect</SelectItem>
                        <SelectItem value="stallion">Stallion</SelectItem>
                        <SelectItem value="not_applicable">Not Applicable</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Conditional: If female */}
              {formData.sex === 'female' && (
                <div className="border-t pt-4">
                  <div className="space-y-2 mb-4">
                    <Label htmlFor="horse_type_female">Horse Type</Label>
                    <Select
                      value={formData.horse_type_female}
                      onValueChange={(v) => handleChange('horse_type_female', v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="racing_broodmare_prospect">Racing/Broodmare Prospect</SelectItem>
                        <SelectItem value="broodmare_prospect">Broodmare Prospect</SelectItem>
                        <SelectItem value="broodmare">Broodmare</SelectItem>
                        <SelectItem value="maiden">Maiden</SelectItem>
                        <SelectItem value="not_applicable">Not Applicable</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="cover_status">Cover Status</Label>
                    <Select
                      value={formData.cover_status}
                      onValueChange={(v) => handleChange('cover_status', v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="aborted">Aborted</SelectItem>
                        <SelectItem value="barren">Barren</SelectItem>
                        <SelectItem value="bred">Bred (Mated)</SelectItem>
                        <SelectItem value="in_foal">In Foal (Pregnant)</SelectItem>
                        <SelectItem value="maiden">Maiden</SelectItem>
                        <SelectItem value="not_pregnant">Not Pregnant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </form>
    </SidebarLayout>
  );
};

export default HorseFormPage;
